import type {ToReactProps} from 'common/util/react';

/**
 * Gap-2 adapter helper for the Panel -> React migration.
 *
 * Flattens nested Panel-form `attrs` / `props` / `on` props into the flat, camelCased props a React
 * FC expects — the reverse of `panel2react`'s `ToReactProps` / `PrefixEventHandlers` (see
 * `common/util/react.tsx`).
 *
 *   {attrs: {'button-styling': {...}}, props: {updateFunc: fn}, on: {change: fn, 'back-click': fn2}}
 *     -> {buttonStyling: {...}, updateFunc: fn, onChange: fn, onBackClick: fn2}
 *
 * Purely additive; wire it in via a per-component `panel.ts` adapter.
 */

type Func = (...args: any[]) => any;

/** Reserved snabbdom/react2panel bridge keys — structural, passed through untouched. */
const RESERVED_BRIDGE_KEYS = new Set<string>([`sel`, `hook`, `class`, `style`, `key`, `params`]);

/** kebab -> camelCase; mirrors the unexported `camelCase` in `common/util/react.tsx` — keep in sync. */
function camelCase(str: string): string {
  const camel = str.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return camel.charAt(0).toLowerCase() + camel.slice(1);
}

/** Reserved bridge keys a Panel-form caller may pass alongside `attrs` / `props` / `on`. */
interface ReservedBridgeKeys {
  sel?: unknown;
  hook?: unknown;
  class?: unknown;
  style?: unknown;
  key?: unknown;
  params?: unknown;
}

/** The nested Panel-form shape a bridged component's callers pass. */
type PanelFormProps<
  JsxProps extends {attrs?: Record<string, any>; props?: Record<string, any>; on?: Record<string, Func>},
> = JsxProps & ReservedBridgeKeys;

/**
 * Flattens nested Panel-form `attrs` / `props` / `on` props into the flat, camelCased props a React
 * FC consumes. Reserved bridge keys are preserved untouched. Event payloads (incl.
 * `CustomEvent.detail`) pass through unchanged — only the handler key is renamed ('change' -> 'onChange').
 */
export function flattenAttrsOn<
  JsxProps extends {attrs?: Record<string, any>; props?: Record<string, any>; on?: Record<string, Func>},
>(panelFormProps?: PanelFormProps<JsxProps> | null): ToReactProps<JsxProps> {
  const {attrs = {}, props = {}, on = {}, ...rest} = panelFormProps ?? ({} as PanelFormProps<JsxProps>);

  const flatAttrs: Record<string, unknown> = {};
  for (const [rawKey, value] of Object.entries(attrs)) {
    const key = camelCase(rawKey);
    // Never let a camelCased attr clobber a reserved bridge key.
    if (RESERVED_BRIDGE_KEYS.has(key)) {
      continue;
    }
    flatAttrs[key] = value;
  }

  const flatProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (RESERVED_BRIDGE_KEYS.has(key)) {
      continue;
    }
    flatProps[key] = value;
  }

  const handlers: Record<string, Func> = {};
  for (const [event, handler] of Object.entries(on)) {
    handlers[`on${camelCase(event).replace(/^./, (c) => c.toUpperCase())}`] = handler;
  }

  return {...rest, ...flatAttrs, ...flatProps, ...handlers} as unknown as ToReactProps<JsxProps>;
}
